import React from "react";
import { AlertTriangle, RotateCw, Trash2, Zap } from "lucide-react";

/**
 * Advanced transfer options — mirror-deletes + retry policy, the two knobs
 * most likely to change run-to-run for an operator tuning a migration.
 *
 * Split off into a reusable component so MigratePC / MigrateServer /
 * MigrateStorage share the exact same controls (and enforce the same
 * mirror-deletes warning). Each caller owns the state; this component is
 * purely presentational.
 *
 * Props:
 *   mirrorDeletes, onMirrorDeletesChange
 *   retries (0-20), onRetriesChange
 *   retryBackoffMs (100-5000), onRetryBackoffMsChange
 *   testId — prefix for data-testid on every input
 */
const BACKOFF_PRESETS = [
    { value: 200, label: "Fast · 200 ms" },
    { value: 400, label: "Default · 400 ms" },
    { value: 800, label: "Patient · 800 ms" },
    { value: 1500, label: "Flaky-WAN · 1.5 s" },
    { value: 3000, label: "Very-flaky · 3 s" },
];

export default function AdvancedTransferOptions({
    mirrorDeletes,
    onMirrorDeletesChange,
    retries,
    onRetriesChange,
    retryBackoffMs,
    onRetryBackoffMsChange,
    testId = "advanced-transfer",
}) {
    const clampedRetries = Math.max(0, Math.min(20, Number(retries) || 0));

    // Show ops-friendly preview of the actual max wait so operators can
    // reason about the policy without doing binary math in their head.
    const maxWaitMs = retries > 0
        ? retryBackoffMs * Math.pow(2, Math.max(0, retries - 1))
        : 0;
    const maxWaitLabel = maxWaitMs >= 1000
        ? `${(maxWaitMs / 1000).toFixed(maxWaitMs >= 10000 ? 0 : 1)} s`
        : `${maxWaitMs} ms`;

    return (
        <div className="space-y-4" data-testid={`${testId}-section`}>
            {/* Mirror deletes */}
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-background p-3.5 transition hover:border-[#5b5ef0]/40">
                <input
                    type="checkbox"
                    checked={mirrorDeletes}
                    onChange={(e) => onMirrorDeletesChange(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 rounded border-border text-[#5b5ef0] focus:ring-[#5b5ef0]"
                    data-testid={`${testId}-mirror-deletes`}
                />
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                        <Trash2 size={14} className="text-[#f59e0b]" />
                        Mirror deletes on destination
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                        rsync <code className="font-mono text-[10px]">--delete</code> semantics.
                        After transfer, removes any destination files no longer present at source —
                        keeps the two sides bit-identical.
                    </p>
                    {mirrorDeletes && (
                        <div
                            className="mt-2 flex items-start gap-2 rounded-md border border-[#f59e0b]/40 bg-[#f59e0b]/5 px-2.5 py-2 text-[11px]"
                            data-testid={`${testId}-mirror-deletes-warning`}
                        >
                            <AlertTriangle size={12} className="mt-0.5 shrink-0 text-[#f59e0b]" />
                            <span>
                                This is <b>destructive</b>. Runs only under this job's destination
                                root so existing files elsewhere are untouched — but removed data
                                is unrecoverable unless versioning is enabled.
                            </span>
                        </div>
                    )}
                </div>
            </label>

            {/* Retry policy */}
            <div className="rounded-lg border border-border bg-background p-3.5">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                    <RotateCw size={14} className="text-[#5b5ef0]" />
                    Retry policy
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {/* Retries */}
                    <div>
                        <label
                            htmlFor={`${testId}-retries`}
                            className="block text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                        >
                            Max attempts
                        </label>
                        <input
                            id={`${testId}-retries`}
                            type="number"
                            min={0}
                            max={20}
                            value={clampedRetries}
                            onChange={(e) => onRetriesChange(
                                Math.max(0, Math.min(20, parseInt(e.target.value || "0", 10) || 0)),
                            )}
                            className="mb-focus mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm tabular-nums"
                            data-testid={`${testId}-retries`}
                        />
                        <p className="mt-1 text-[10px] text-muted-foreground">
                            0 disables retry. 3 is the default.
                        </p>
                    </div>

                    {/* Backoff */}
                    <div>
                        <label
                            htmlFor={`${testId}-backoff`}
                            className="block text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                        >
                            Base backoff
                        </label>
                        <select
                            id={`${testId}-backoff`}
                            value={retryBackoffMs}
                            onChange={(e) => onRetryBackoffMsChange(Number(e.target.value))}
                            className="mb-focus mt-1 w-full appearance-none rounded-md border border-input bg-background px-3 py-2 text-sm"
                            data-testid={`${testId}-backoff`}
                        >
                            {BACKOFF_PRESETS.map((p) => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </select>
                        <p className="mt-1 text-[10px] text-muted-foreground">
                            Exponential: 2× per attempt + jitter.
                        </p>
                    </div>
                </div>

                {clampedRetries > 0 && (
                    <div
                        className="mt-3 flex items-center gap-2 rounded-md bg-muted/40 px-2.5 py-2 text-[11px] text-muted-foreground"
                        data-testid={`${testId}-retry-summary`}
                    >
                        <Zap size={11} className="text-[#5b5ef0]" />
                        <span>
                            Worst-case wait on the last attempt is <b>{maxWaitLabel}</b>.
                            A single failed block will retry up to <b>{clampedRetries}</b>× before the job marks it.
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}
